/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { X, Tag, MessageSquare, MapPin, BadgeEuro, Package, Scale, Trash2, Plus, Star, Loader2, CheckCircle2, Truck } from 'lucide-react';
import { useCategories } from './CategoriesContext';
import { useMunicipalities } from './MunicipalitiesContext';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Select from 'react-select';
import { productsService } from '../api';
import { useToast } from './Toast';
import Modal from './Modal';
import { filterByPrefix, selectStyles, selectClassNames } from '../utils/selectStyles';
import { trackEvent } from '../utils/analytics';

const MAX_IMAGES = 5;
const MAX_IMAGE_BYTES = 9 * 1024 * 1024;
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];

const UNIT_OPTIONS = [
  { value: 'kg', label: 'po kilogramu (kg)' },
  { value: 'kom', label: 'po komadu (kom)' },
  { value: 'litra', label: 'po litru (l)' },
  { value: 'teglu', label: 'po tegli' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Aktivan' },
  { value: 'sold', label: 'Rasprodat' },
];

const DELIVERY_OPTIONS = [
  { value: 'licno', label: 'Lično' },
  { value: 'adresa', label: 'Na adresu' },
  { value: 'posta', label: 'Poštom' },
];

const initialForm = {
  name: '',
  category: '',
  price: '',
  unit: 'kg',
  description: '',
  municipality: '',
  deliveryMethod: '',
  status: 'active',
};

export default function AdModal({ isOpen, onClose, productToEdit, onSaved }) {
  const isEditing = !!productToEdit;
  const fileInputRef = useRef(null);
  const toast = useToast();
  const { names: categoryNames } = useCategories();
  const categoryOptions = categoryNames.map((c) => ({ value: c, label: c }));
  const { names: gradovi } = useMunicipalities();
  const gradoviOptions = useMemo(() => gradovi.map((g) => ({ value: g, label: g })), [gradovi]);

  const [form, setForm] = useState(initialForm);
  const [newImages, setNewImages] = useState([]);     // File[]
  const [newPreviews, setNewPreviews] = useState([]); // blob URL[] (paralelno sa newImages)
  const [existingImages, setExistingImages] = useState([]); // {id, url}[] iz productToEdit
  const [mainImageIndex, setMainImageIndex] = useState(0); // indeks u kombinovanoj listi (existing + new)
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetState = useCallback(() => {
    setForm(initialForm);
    setNewImages([]);
    setNewPreviews((prev) => {
      prev.forEach((url) => URL.revokeObjectURL(url));
      return [];
    });
    setExistingImages([]);
    setMainImageIndex(0);
    setErrors({});
    setSubmitError(null);
    setIsSubmitting(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    if (productToEdit) {
      setForm({
        name: productToEdit.name ?? '',
        category: productToEdit.category ?? '',
        price: productToEdit.price ?? '',
        unit: productToEdit.unit ?? 'kg',
        description: productToEdit.description ?? '',
        municipality: productToEdit.municipality ?? '',
        deliveryMethod: productToEdit.deliveryMethod ?? '',
        status: productToEdit.statusKey === 'sold' ? 'sold' : 'active',
      });

      const imgs = Array.isArray(productToEdit.images) && productToEdit.images.length > 0
        ? productToEdit.images.map((it) => (typeof it === 'string' ? { id: it, url: it } : it))
        : productToEdit.image
          ? [{ id: productToEdit.image, url: productToEdit.image }]
          : [];
      setExistingImages(imgs);
      setMainImageIndex(0);
    } else {
      setForm(initialForm);
      setExistingImages([]);
      setMainImageIndex(0);
    }
    setNewImages([]);
    setNewPreviews((prev) => {
      prev.forEach((url) => URL.revokeObjectURL(url));
      return [];
    });
    setErrors({});
    setSubmitError(null);
  }, [isOpen, productToEdit]);

  useEffect(() => {
    return () => {
      newPreviews.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalImages = existingImages.length + newImages.length;

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;

    const remaining = MAX_IMAGES - totalImages;
    const accepted = [];
    const rejected = [];

    for (const file of files.slice(0, remaining)) {
      if (!ALLOWED_MIME.includes(file.type)) {
        rejected.push(`${file.name}: nepodržan format`);
        continue;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        rejected.push(`${file.name}: veća od 9 MB`);
        continue;
      }
      accepted.push(file);
    }

    if (accepted.length > 0) {
      setNewImages((prev) => [...prev, ...accepted]);
      setNewPreviews((prev) => [...prev, ...accepted.map((f) => URL.createObjectURL(f))]);
      if (errors.images) setErrors((prev) => ({ ...prev, images: undefined }));
    }
    if (rejected.length > 0) {
      setErrors((prev) => ({ ...prev, images: rejected.join(', ') }));
    }
  };

  const removeImage = (index) => {
    if (index < existingImages.length) {
      setExistingImages((prev) => prev.filter((_, i) => i !== index));
    } else {
      const newIdx = index - existingImages.length;
      setNewImages((prev) => prev.filter((_, i) => i !== newIdx));
      setNewPreviews((prev) => {
        const url = prev[newIdx];
        if (url) URL.revokeObjectURL(url);
        return prev.filter((_, i) => i !== newIdx);
      });
    }
    setMainImageIndex((prev) => {
      if (prev === index) return 0;
      if (prev > index) return prev - 1;
      return prev;
    });
  };

  const setAsMain = (index) => setMainImageIndex(index);

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Unesite naziv proizvoda';
    if (!form.category) next.category = 'Izaberite kategoriju';
    const priceNum = Number(form.price);
    if (!form.price || Number.isNaN(priceNum) || priceNum <= 0) next.price = 'Unesite ispravnu cenu';
    if (!form.description.trim()) next.description = 'Unesite opis';
    if (!form.municipality) next.municipality = 'Izaberite opštinu';
    if (!form.deliveryMethod) next.deliveryMethod = 'Izaberite način preuzimanja';
    if (totalImages === 0) next.images = 'Dodajte bar jednu fotografiju';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validate()) return;

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      const fd = new FormData();
      fd.append('name', form.name.trim());
      fd.append('category', form.category);
      fd.append('price', String(Number(form.price)));
      fd.append('unit', form.unit);
      fd.append('description', form.description.trim());
      fd.append('municipality', form.municipality);
      fd.append('deliveryMethod', form.deliveryMethod);
      if (isEditing) fd.append('status', form.status);
      fd.append('mainImageIndex', String(mainImageIndex));
      newImages.forEach((file) => fd.append('images', file));
      if (isEditing) {
        existingImages.forEach((img) => fd.append('keepImages[]', img.id ?? img.url));
      }
      const res = isEditing
        ? await productsService.update(productToEdit.id, fd)
        : await productsService.create(fd);

      trackEvent(isEditing ? 'ad_update' : 'ad_create', {
        category: form.category,
        municipality: form.municipality,
        price: Number(form.price),
      });
      if (onSaved) onSaved(res.data);
      window.dispatchEvent(new CustomEvent('product-saved', { detail: { isEditing, data: res.data } }));
      toast.success(isEditing ? 'Oglas je uspešno ažuriran.' : 'Oglas je uspešno objavljen.');
      resetState();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Greška prilikom čuvanja oglasa.';
      setSubmitError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const combinedPreviews = [
    ...existingImages.map((img) => img.url),
    ...newPreviews,
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      label={isEditing ? 'Izmena oglasa' : 'Novi oglas'}
      panelClassName="w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden"
    >
            {isSubmitting && (
              <div className="absolute inset-0 z-20 bg-white/70 backdrop-blur-sm flex flex-col items-center justify-center gap-3 pointer-events-auto">
                <Loader2 className="text-sage-600 animate-spin" size={36} />
                <p className="text-sm font-bold text-earth-900">
                  {isEditing ? 'Čuvanje izmena...' : 'Objavljivanje oglasa...'}
                </p>
                <p className="text-xs text-sage-600">Učitavanje slika u toku, sačekajte trenutak.</p>
              </div>
            )}
            <div className="p-8 sm:p-10 max-h-[88dvh] overflow-y-auto custom-scrollbar">
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                className="absolute top-6 right-6 p-2 text-sage-400 hover:text-sage-600 hover:bg-sage-50 rounded-full transition-colors z-10 disabled:opacity-40"
              >
                <X size={20} />
              </button>

              <div className="mb-8">
                <h2 className="text-3xl font-serif font-bold text-earth-900 mb-2">
                  {isEditing ? 'Izmeni oglas' : 'Postavi novi oglas'}
                </h2>
                <p className="text-sage-600">
                  {isEditing
                    ? 'Ažurirajte podatke o vašem proizvodu kako biste privukli više kupaca.'
                    : 'Unesite detalje o vašem domaćem proizvodu koji želite da prodate.'}
                </p>
              </div>

              <form className="space-y-6" onSubmit={handleSubmit} noValidate>
                {/* Image Upload Area */}
                <div>
                  <label className="block text-sm font-bold text-earth-900 mb-3 ml-1">Fotografije proizvoda (maks. {MAX_IMAGES})</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {combinedPreviews.map((img, idx) => (
                      <div key={idx} className="relative aspect-square rounded-2xl overflow-hidden group border border-sage-100 shadow-sm">
                        <img src={img} alt={`Preview ${idx}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          aria-label="Ukloni sliku"
                          className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 size={14} />
                        </button>
                        {idx !== mainImageIndex && (
                          <button
                            type="button"
                            onClick={() => setAsMain(idx)}
                            aria-label="Postavi kao glavnu sliku"
                            className="absolute top-2 left-2 p-1.5 bg-white/90 text-sage-700 rounded-lg shadow-sm opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                            title="Postavi kao glavnu sliku"
                          >
                            <Star size={14} />
                          </button>
                        )}
                        {idx === mainImageIndex && (
                          <div className="absolute bottom-0 left-0 right-0 bg-sage-600/90 text-white text-[11px] py-1 text-center font-bold">
                            Glavna slika
                          </div>
                        )}
                      </div>
                    ))}

                    {totalImages < MAX_IMAGES && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="aspect-square bg-sage-50 border-2 border-dashed border-sage-200 rounded-2xl flex flex-col items-center justify-center gap-2 hover:bg-sage-100 hover:border-sage-300 transition-all text-sage-600"
                      >
                        <Plus size={24} />
                        <span className="text-xs font-bold">Dodaj sliku</span>
                      </button>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    multiple
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                  />
                  {errors.images ? (
                    <p className="mt-2 text-xs text-red-600 ml-1">{errors.images}</p>
                  ) : (
                    <p className="mt-2 text-xs text-sage-500 ml-1 italic">Preporučujemo svetle i jasne fotografije vaših proizvoda.</p>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-earth-900 mb-2 ml-1">Naziv proizvoda</label>
                    <div className="relative">
                      <Tag className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400" size={18} />
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setField('name', e.target.value)}
                        placeholder="npr. Domaći med"
                        className="w-full bg-sage-50 border border-sage-100 rounded-2xl py-3.5 pl-12 pr-4 focus:ring-2 focus:ring-sage-200 outline-none transition-all"
                      />
                    </div>
                    {errors.name && <p className="mt-1 text-xs text-red-600 ml-1">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-earth-900 mb-2 ml-1">Kategorija</label>
                    <div className="relative">
                      <Package className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400 z-10" size={18} />
                      <Select
                        options={categoryOptions}
                        value={categoryOptions.find((o) => o.value === form.category) || null}
                        onChange={(opt) => setField('category', opt?.value || '')}
                        placeholder="Izaberite kategoriju"
                        isSearchable
                        isClearable
                        filterOption={filterByPrefix}
                        noOptionsMessage={() => 'Nema rezultata'}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                        menuPlacement="auto"
                        styles={selectStyles}
                        unstyled
                        classNames={selectClassNames}
                      />
                    </div>
                    {errors.category && <p className="mt-1 text-xs text-red-600 ml-1">{errors.category}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-earth-900 mb-2 ml-1">Cena (RSD)</label>
                    <div className="relative">
                      <BadgeEuro className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400" size={18} />
                      <input
                        type="number"
                        min="0"
                        value={form.price}
                        onChange={(e) => setField('price', e.target.value)}
                        placeholder="0"
                        className="w-full bg-sage-50 border border-sage-100 rounded-2xl py-3.5 pl-12 pr-4 focus:ring-2 focus:ring-sage-200 outline-none transition-all"
                      />
                    </div>
                    {errors.price && <p className="mt-1 text-xs text-red-600 ml-1">{errors.price}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-earth-900 mb-2 ml-1">Jedinica mere</label>
                    <div className="relative">
                      <Scale className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400 z-10" size={18} />
                      <Select
                        options={UNIT_OPTIONS}
                        value={UNIT_OPTIONS.find((o) => o.value === form.unit) || null}
                        onChange={(opt) => setField('unit', opt?.value || 'kg')}
                        placeholder="Izaberite jedinicu mere"
                        isSearchable={false}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                        menuPlacement="auto"
                        styles={selectStyles}
                        unstyled
                        classNames={selectClassNames}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-earth-900 mb-2 ml-1">Opis proizvoda</label>
                  <div className="relative">
                    <MessageSquare className="absolute left-4 top-4 text-sage-400" size={18} />
                    <textarea
                      value={form.description}
                      onChange={(e) => setField('description', e.target.value)}
                      placeholder="Opišite vaš proizvod (način gajenja, svežina, sorta...)"
                      rows={4}
                      className="w-full bg-sage-50 border border-sage-100 rounded-3xl py-3.5 pl-12 pr-4 focus:ring-2 focus:ring-sage-200 outline-none transition-all resize-none"
                    ></textarea>
                  </div>
                  {errors.description && <p className="mt-1 text-xs text-red-600 ml-1">{errors.description}</p>}
                </div>

                <div>
                  <label className="block text-sm font-bold text-earth-900 mb-2 ml-1">Lokacija</label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400 z-10" size={18} />
                    <Select
                      options={gradoviOptions}
                      value={gradoviOptions.find((o) => o.value === form.municipality) || null}
                      onChange={(opt) => setField('municipality', opt?.value || '')}
                      placeholder="Izaberite opštinu (kucajte za pretragu)"
                      isSearchable
                      isClearable
                      filterOption={filterByPrefix}
                      noOptionsMessage={() => 'Nema rezultata'}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition="fixed"
                      menuPlacement="auto"
                      styles={selectStyles}
                      unstyled
                      classNames={selectClassNames}
                    />
                  </div>
                  {errors.municipality && <p className="mt-1 text-xs text-red-600 ml-1">{errors.municipality}</p>}
                </div>

                <div>
                  <label className="block text-sm font-bold text-earth-900 mb-2 ml-1">Način preuzimanja</label>
                  <div className="relative">
                    <Truck className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400 z-10" size={18} />
                    <Select
                      options={DELIVERY_OPTIONS}
                      value={DELIVERY_OPTIONS.find((o) => o.value === form.deliveryMethod) || null}
                      onChange={(opt) => setField('deliveryMethod', opt?.value || '')}
                      placeholder="Izaberite način preuzimanja"
                      isSearchable={false}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition="fixed"
                      menuPlacement="auto"
                      styles={selectStyles}
                      unstyled
                      classNames={selectClassNames}
                    />
                  </div>
                  {errors.deliveryMethod && <p className="mt-1 text-xs text-red-600 ml-1">{errors.deliveryMethod}</p>}
                </div>

                {isEditing && (
                  <div>
                    <label className="block text-sm font-bold text-earth-900 mb-2 ml-1">Status oglasa</label>
                    <div className="relative">
                      <CheckCircle2 className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400 z-10" size={18} />
                      <Select
                        options={STATUS_OPTIONS}
                        value={STATUS_OPTIONS.find((o) => o.value === form.status) || STATUS_OPTIONS[0]}
                        onChange={(opt) => setField('status', opt?.value || 'active')}
                        placeholder="Izaberite status"
                        isSearchable={false}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                        menuPlacement="auto"
                        styles={selectStyles}
                        unstyled
                        classNames={selectClassNames}
                      />
                    </div>
                    <p className="mt-1.5 text-xs text-sage-500 ml-1 italic">
                      Postavi na "Rasprodat" kad više nemaš ovaj proizvod — oglas ostaje vidljiv ali sa oznakom.
                    </p>
                  </div>
                )}

                {submitError && (
                  <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-2xl text-sm text-red-700">
                    {submitError}
                  </div>
                )}

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="flex-1 py-4 border border-sage-200 text-sage-800 rounded-2xl font-bold hover:bg-sage-50 transition-all disabled:opacity-50"
                  >
                    Odustani
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-[2] py-4 bg-sage-600 text-white rounded-2xl font-bold shadow-lg shadow-sage-600/20 hover:bg-sage-700 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting && <Loader2 className="animate-spin" size={18} />}
                    {isSubmitting
                      ? (isEditing ? 'Čuvanje...' : 'Objavljivanje...')
                      : (isEditing ? 'Sačuvaj izmene' : 'Objavi oglas')}
                  </button>
                </div>
              </form>
            </div>
    </Modal>
  );
}
